import React from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { CheckCircle2, XCircle, Clock, ChefHat, Users, MessageSquare, Loader2, AlertCircle, Hand, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { StatusBadge, PriorityBadge } from './TaskBadges';
import { stationLabel, serviceLabel } from '../../utils/kitchenTasks';

const SWIPE_THRESHOLD = 110;
const ACTIVE = ['a_faire', 'en_cours', 'refusee'];
const BAR = { en_cours: 'bg-orange-500', a_valider: 'bg-violet-500', validee: 'bg-emerald-500', terminee: 'bg-emerald-500', refusee: 'bg-red-500', non_realisee: 'bg-red-500', annulee: 'bg-gray-300' };

// mode: 'mine' (swipe), 'unassigned' (take), 'other' (read-only), 'readonly' (done)
const TaskCard = ({ task, mode = 'readonly', onSwipeRight, onSwipeLeft, onTake, busy, locked }) => {
  const x = useMotionValue(0);
  const bg = useTransform(x, [-SWIPE_THRESHOLD, 0, SWIPE_THRESHOLD], ['rgba(220,38,38,0.18)', 'rgba(255,255,255,0)', 'rgba(16,185,129,0.18)']);
  const rightOpacity = useTransform(x, [20, SWIPE_THRESHOLD], [0, 1]);
  const leftOpacity = useTransform(x, [-SWIPE_THRESHOLD, -20], [1, 0]);
  const rotate = useTransform(x, [-300, 300], [-4, 4]);

  const isActive = ACTIVE.includes(task.status);
  const swipeable = mode === 'mine' && isActive && !busy && !locked;
  const dimmed = mode === 'other' && isActive;
  const border = task.status === 'refusee' ? 'border-red-300' : task.priority === 'urgente' ? 'border-red-400' : task.is_late ? 'border-red-300' : 'border-gray-200';

  const onDragEnd = (_, info) => {
    if (!swipeable) return;
    if (info.offset.x > SWIPE_THRESHOLD) onSwipeRight?.(task);
    else if (info.offset.x < -SWIPE_THRESHOLD) onSwipeLeft?.(task);
  };

  return (
    <motion.div className="relative rounded-3xl" style={{ backgroundColor: bg }} data-testid={`kitchen-task-wrapper-${task.id}`}>
      {swipeable && (
        <div className="absolute inset-0 flex items-center justify-between px-6 pointer-events-none">
          <motion.span style={{ opacity: leftOpacity }} className="flex items-center gap-1.5 text-red-700 font-bold text-sm"><XCircle size={20} />Non réalisée</motion.span>
          <motion.span style={{ opacity: rightOpacity }} className="flex items-center gap-1.5 text-emerald-700 font-bold text-sm">Terminée<CheckCircle2 size={20} /></motion.span>
        </div>
      )}
      <motion.div
        drag={swipeable ? 'x' : false}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.7}
        dragSnapToOrigin
        onDragEnd={onDragEnd}
        style={{ x, rotate }}
        whileDrag={{ scale: 1.02 }}
        className={`rounded-3xl ${swipeable ? 'cursor-grab active:cursor-grabbing touch-pan-y' : ''}`}
        data-testid={`kitchen-task-card-${task.id}`}
        data-mode={mode}
      >
        <div className={`bg-white border-2 ${border} rounded-3xl overflow-hidden ${dimmed ? 'opacity-60 grayscale-[30%]' : ''}`}>
          <div className={`h-2 ${BAR[task.status] || 'bg-blue-500'}`} />
          <div className="p-5 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={task.status} isLate={task.is_late} />
              <PriorityBadge priority={task.priority} />
              <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 border border-gray-200 text-gray-600">{stationLabel(task.category_name)}</span>
              {dimmed && <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-gray-800 text-white ml-auto" data-testid={`kitchen-task-readonly-${task.id}`}><Lock size={11} />Lecture seule</span>}
            </div>
            <h3 className="font-heading text-xl md:text-2xl font-bold text-gray-900" data-testid={`kitchen-task-title-${task.id}`}>{task.title}</h3>
            <div className="flex flex-wrap gap-2 text-sm text-gray-600">
              {task.planned_quantity != null && <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-800 px-3 py-1 rounded-full font-medium"><ChefHat size={14} />{task.planned_quantity} {task.unit}</span>}
              <span className="inline-flex items-center gap-1.5 bg-gray-100 px-3 py-1 rounded-full">{serviceLabel(task.service)}</span>
              {task.due_time && <span className="inline-flex items-center gap-1.5 bg-gray-100 px-3 py-1 rounded-full"><Clock size={14} />avant {task.due_time}</span>}
            </div>
            {task.description && (
              <div className="bg-neon-yellow/5 border border-neon-yellow/30 rounded-xl p-3 flex gap-2 text-sm text-amber-900"><MessageSquare size={16} className="flex-shrink-0 mt-0.5" /><span>{task.description}</span></div>
            )}
            {task.status === 'refusee' && task.rejection_reason && (
              <div className="bg-red-50 border border-red-300 rounded-xl p-3 flex gap-2 text-sm text-red-800" data-testid={`kitchen-task-rejection-${task.id}`}><AlertCircle size={16} className="flex-shrink-0 mt-0.5" /><span><strong>À corriger :</strong> {task.rejection_reason}</span></div>
            )}
            {task.status === 'non_realisee' && (
              <div className="bg-red-50 border border-red-300 rounded-xl p-3 flex gap-2 text-sm text-red-800" data-testid={`kitchen-task-not-done-${task.id}`}><XCircle size={16} className="flex-shrink-0 mt-0.5" /><span><strong>Non réalisée :</strong> {task.not_done_reason}</span></div>
            )}
            {task.status === 'a_valider' && (
              <p className="text-sm text-violet-700">En attente de validation par le chef{task.actual_quantity != null ? ` · ${task.actual_quantity} ${task.actual_unit || task.unit || ''} produits` : ''}.</p>
            )}
            {task.status === 'terminee' && (
              <p className="text-sm text-emerald-700" data-testid={`kitchen-task-done-${task.id}`}>Terminée{task.actual_quantity != null ? ` · ${task.actual_quantity} ${task.actual_unit || task.unit || ''} produits` : ''}{task.completed_at ? ` · ${new Date(task.completed_at).toLocaleTimeString('fr-FR', { timeZone: 'Europe/Zurich', hour: '2-digit', minute: '2-digit' })}` : ''}</p>
            )}
            {task.status === 'validee' && task.chef_comment && <p className="text-sm text-emerald-700">Chef : {task.chef_comment}</p>}
            <div className="flex items-center justify-between text-xs text-gray-500 pt-1">
              <span className="inline-flex items-center gap-1" data-testid={`kitchen-task-assignees-${task.id}`}><Users size={12} />{(task.assigned_user_names || []).join(', ') || 'Non affectée'}</span>
              <span>Créée par {task.created_by_name}</span>
            </div>

            {mode === 'unassigned' && isActive && (
              <Button onClick={() => onTake?.(task)} disabled={busy || locked} className="w-full h-12 rounded-full bg-neon-pink hover:bg-neon-pink/90 text-gray-900 font-semibold text-base" data-testid={`take-task-btn-${task.id}`}>
                {busy ? <Loader2 size={18} className="animate-spin mr-2" /> : <Hand size={18} className="mr-2" />}Prendre cette tâche
              </Button>
            )}
            {mode === 'mine' && isActive && (
              <div className="space-y-2 pt-1">
                <p className="text-center text-xs text-gray-400 select-none">← Glisser à gauche : non réalisée · Glisser à droite : terminée →</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => onSwipeLeft?.(task)} disabled={busy || locked} className="h-11 rounded-full border-red-300 text-red-700 hover:bg-red-50 font-semibold" data-testid={`not-done-task-btn-${task.id}`}>
                    <XCircle size={16} className="mr-1.5" />Non réalisée
                  </Button>
                  <Button onClick={() => onSwipeRight?.(task)} disabled={busy || locked} className="h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid={`complete-task-btn-${task.id}`}>
                    {busy ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <CheckCircle2 size={16} className="mr-1.5" />}Terminée
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TaskCard;
